import { Controller } from '@hotwired/stimulus'

import { fetchWithHeaders } from 'utils'

import { GranularAllowancesError } from '../errors'
import { type HandlesPercentile } from '../interfaces'

// Connects to data-controller="granular-allowances-estimate"
export default class extends Controller implements HandlesPercentile {
  static targets = [
    'percentileDisplay',
    'estimateRowTemplate',
    'estimateForm',
    'estimateFormTotal',
    'estimateFormAverage',
  ]

  declare readonly percentileDisplayTarget: HTMLSpanElement
  declare readonly estimateRowTemplateTarget: HTMLTemplateElement
  declare readonly estimateFormTarget: HTMLDivElement
  declare readonly estimateFormTotalTarget: HTMLSpanElement
  declare readonly hasEstimateFormTotalTarget: boolean
  declare readonly estimateFormAverageTarget: HTMLSpanElement
  declare readonly hasEstimateFormAverageTarget: boolean

  static values = {
    initialPercentile: Number,
  }

  declare initialPercentileValue: number
  declare hasInitialPercentileValue: boolean

  private allowancesEstimateCache: Record<string, Record<string, Record<number, number>>> = {}
  private currentPercentile = 50

  initialize = (): void => {
    if (this.hasInitialPercentileValue) {
      this.currentPercentile = this.initialPercentileValue
    }
  }

  updatePercentile = (percentile: number): void => {
    this.currentPercentile = percentile
    this.percentileDisplayTarget.innerHTML = percentile.toString()

    this.updateTotal()
  }

  updateTotal = (): void => {
    let rowCount = 0
    let newTotal = 0
    for (const row of this.estimateFormTarget.children) {
      if (row.id === 'employee-row') {
        newTotal += this.calculateRowEstimate(row as HTMLDivElement)
        rowCount += 1
      }
    }

    // To fix divide by 0 errors
    if (rowCount === 0) rowCount = 1

    if (this.hasEstimateFormTotalTarget) {
      this.estimateFormTotalTarget.innerHTML = newTotal.toString()
    }
    if (this.hasEstimateFormAverageTarget) {
      this.estimateFormAverageTarget.innerHTML = Math.round(newTotal / rowCount).toString()
    }
  }

  addToEstimateForm = (event: Event) => {
    const target = event.target as HTMLElement
    const row = target.closest('#employee-row-form')!
    const zipField = row.querySelector('#zip_code') as HTMLInputElement
    const ageField = row.querySelector('#age') as HTMLInputElement
    const marriedField = row.querySelector('#married') as HTMLInputElement
    const dependentsField = row.querySelector('#dependents') as HTMLInputElement

    zipField.setCustomValidity('')
    dependentsField.setCustomValidity('')

    if (zipField.value.length !== 5) {
      zipField.setCustomValidity('Format: 12345')
    } else if (isNaN(parseInt(zipField.value))) {
      zipField.setCustomValidity('ZIP must be a number')
    }
    zipField.reportValidity()

    if (isNaN(parseInt(dependentsField.value))) {
      dependentsField.setCustomValidity('Dependents must be a number')
    }
    dependentsField.reportValidity()

    if (zipField.checkValidity() && dependentsField.checkValidity()) {
      const newRow = document
        .importNode(this.estimateRowTemplateTarget.content, true)
        .firstElementChild as HTMLDivElement
      newRow.querySelector('#zip_code')!.innerHTML = zipField.value
      newRow.querySelector('#age')!.innerHTML = ageField.value
      newRow.querySelector('#married')!.innerHTML = marriedField.value
      newRow.querySelector('#dependents')!.innerHTML = dependentsField.value
      this.estimateFormTarget.insertBefore(newRow, row)
      this.estimateFormTarget.scrollTop = this.estimateFormTarget.scrollHeight

      zipField.value = ''
      ageField.value = '18'
      marriedField.value = 'No'
      dependentsField.value = '0'

      this.handleNewRow(newRow)
    }
  }

  removeFromEstimateForm = (event: Event) => {
    const target = event.target as HTMLElement
    const row = target.closest('#employee-row')! as HTMLElement

    // Wait for rows to be loaded or in a failed state before enabling removal
    if (row.dataset.status !== 'loading') {
      row.remove()

      // Only subtract from total if row was loaded (meaning we added the row allowance to the total)
      if (row.dataset.status === 'loaded') {
        this.updateTotal()
      }
    }
  }

  handleNewRow = (row: HTMLDivElement) => {
    this.fetchAndAddToTotal(row)
  }

  fetchAndAddToTotal = (row: HTMLDivElement) => {
    row.dataset.status = 'loading'

    // Grab data from row
    const zipCode = row.querySelector('#zip_code')!.innerHTML
    const age = row.querySelector('#age')!.innerHTML

    // Helper to fetch allowance for a given age
    // It pulls from the cache if able, otherwise it fetches from the server
    // We wrap it all in a Promise so it's easier to use Promise.all and we dont have
    // to use as many conditionals.
    const fetchAllowance = (age: string): Promise<Record<number, number>> => {
      if (this.allowancesEstimateCache[zipCode] && this.allowancesEstimateCache[zipCode][age]) {
        return Promise.resolve(this.allowancesEstimateCache[zipCode][age])
      } else {
        return fetchWithHeaders(`/public/granular_allowances?zip_code=${zipCode}&age=${age}&year=${new Date().getFullYear()}`)
          .then(response => {
            if (response.ok) {
              return response.json()
            } else {
              throw new GranularAllowancesError(response.status.toString())
            }
          })
          .then((body: Record<number, number>) => {
            if (!this.allowancesEstimateCache[zipCode]) {
              this.allowancesEstimateCache[zipCode] = {}
            }

            this.allowancesEstimateCache[zipCode][age] = body
            return body
          })
      }
    }

    // Build list of ages to grab (add dependent base age if dependents > 0)
    const agesToFetch = [age]

    // Once allowances are fetched, we can mark the row as loaded and calculate the estimate
    Promise.all(agesToFetch.map((age) => fetchAllowance(age)))
      .then((_results) => {
        row.dataset.status = 'loaded'
        this.updateTotal()
      })
      .catch((error: GranularAllowancesError) => {
        if (error.message === '404') {
          this.handleInvalidRow(row)
        } else {
          this.handleFailedRow(row)
          throw new GranularAllowancesError(`Failed to fetch ZIP code ${zipCode} (status ${error.message})`)
        }
      })
  }

  // Mark the row as invalid to signify that the ZIP code is not valid
  handleInvalidRow = (row: HTMLDivElement) => {
    row.dataset.status = 'invalid'
    row.classList.add('bg-red-100', 'rounded-md')

    const zipCol = row.querySelector('#zip_code')!
    zipCol.innerHTML = `
      <span class="text-red-500">
        ${zipCol.innerHTML}
        <br>
        <span class="text-xs">Unknown ZIP</span>
      </span>
    `
  }

  // We could eventually retry here, but for now we just mark the row as failed and move on
  handleFailedRow = (row: HTMLDivElement) => {
    row.dataset.status = 'failed'
    row.classList.add('bg-red-100', 'rounded-md')

    const allowanceCol = row.querySelector('#allowance')!
    allowanceCol.parentElement!.innerHTML = `
      <span>
        <span class="text-xs text-red-500">Error, please retry</span>
      </span>
    `
  }

  calculateRowEstimate = (row: HTMLDivElement): number => {
    // Only consider loaded rows, otherwise just return 0
    if (row.dataset.status === 'loaded') {
      const zipCode = row.querySelector('#zip_code')!.innerHTML
      const age = row.querySelector('#age')!.innerHTML
      const childCount = parseFloat(row.querySelector('#dependents')!.innerHTML)

      // Grab allowance for zip and age to use for participant and spouse
      const baseAllowance = this.allowancesEstimateCache[zipCode][age][this.currentPercentile]
      const spouseCount = row.querySelector('#married')!.innerHTML === 'Yes' ? 1 : 0
      const dependentCount = spouseCount + childCount

      let calculatedAllowance = baseAllowance

      // Factor in dependents using dependent base age (18)
      if (dependentCount > 0) {
        const dependentsMult = 1 + Math.min((dependentCount - 1), 3) * 0.5

        calculatedAllowance += Math.round(dependentsMult * baseAllowance)
      }

      const allowanceDisplay = row.querySelector('#allowance')!
      allowanceDisplay.innerHTML = `${calculatedAllowance}`

      return calculatedAllowance
    }
    return 0
  }

  addToTotal = (allowance: number) => {
    let total = parseInt(this.estimateFormTotalTarget.innerHTML!)
    total += allowance

    this.estimateFormTotalTarget.innerHTML = `${total}`
  }
}
